'use client';

import { Button } from '@/components/Button';
import { FormInput } from '@/components/FormInput';
import { useResetPasswordResetPassword } from '@/hooks/useUserQuery';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { MoonLoader } from 'react-spinners';
import { z } from 'zod';

type Props = {
  token: string;
};

export const ResetPasswordPasswordChangeForm = ({ token }: Props) => {
  const t = useTranslations();
  const { mutate: resetPassword, isPending } = useResetPasswordResetPassword();

  const FormValuesSchema = z
    .object({
      password: z
        .string()
        .max(255, {
          message: t('forms.error-password-max-length'),
        })
        .refine(
          (value) =>
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/.test(
              value,
            ),
          t('forms.error-password-strength'),
        ),
      passwordConfirmation: z.string(),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: t('forms.error-password-repeat-match'),
      path: ['passwordConfirmation'],
    });

  type FormValues = z.infer<typeof FormValuesSchema>;

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormValuesSchema),
  });

  const onSubmit = handleSubmit(async ({ password }) => {
    resetPassword({ token, password });
  });

  return (
    <form className="space-y-6" onSubmit={onSubmit} noValidate>
      <FormInput
        {...register('password')}
        data-testid="reset-password-new"
        label={t('reset-password-token.label-new-password')}
        type="password"
        errorMessage={errors.password?.message}
      />

      <FormInput
        {...register('passwordConfirmation')}
        data-testid="reset-password-confirmation"
        label={t('reset-password-token.label-new-password-confirmation')}
        type="password"
        errorMessage={errors.passwordConfirmation?.message}
      />

      <div>
        <Button
          type="submit"
          disabled={isPending}
          data-testid="reset-password-submit"
          className="mt-10 flex w-full justify-center rounded-full bg-gray-900 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 "
        >
          <MoonLoader
            size={19}
            color="#fff"
            loading={isPending}
            aria-label={t('general.label-loading-spinner')}
          />
          {!isPending && t('reset-password-token.label-reset-password')}
        </Button>
      </div>
    </form>
  );
};
